import React, { useMemo } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
// Components
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import {
  Box,
  Button,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import {
  Title,
  Divider,
  DetailList,
} from '../../../components/ProfileVisualComponents';
import IconTitle from '../../../components/ProfileVisualComponents/IconTitle';
import FormattedDateOrDashes from '../../../components/FormattedDateOrDashes';
import FloorPlanDetailRow from '../../../components/FloorPlanDetailRow';

// Hooks
import useAffordableWaitlistValidations from '../../ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';

// Types
import type { Prospect } from '../../ProspectProfile/types';
import type { FloorPlan } from '../../ManageFloorPlans/types';

// Utils
import { findIndex, sortBy } from 'ramda';

// Constants
import messages from './messages';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Props = {
  currentRecord: Prospect,
  floorPlans: ?(FloorPlan[]),
  numberOfScheduledContacts: number,
  numberOfCompletedContacts: number,
  users: Array<Object>,
  handleAssignedToChange: Function,
  handleConvertToApplicant: Function,
  history: {
    push: Function,
  },
  intl: Object,
};

const LabelProfileDetails = styled.label.attrs({
  className: 'profile-details',
})``;

export const Snapshot = ({
  currentRecord,
  floorPlans,
  numberOfScheduledContacts,
  numberOfCompletedContacts,
  users,
  handleAssignedToChange,
  handleConvertToApplicant,
  history,
  intl,
  initialValues,
}: Props) => {
  const nextActivity = currentRecord.nextActivity || { startTime: undefined };
  const {
    affordableProgramsArr = [],
    unitType,
    preferredFloorPlanId,
    unit,
  } = currentRecord.prospectPreferences || {};

  const { affordableWaitlistV2 } = useFlags();

  const {
    affordableWaitlistFlag,
    hasAffordableWaitlistPermissionAdd,
    accountThisPropertyForWaitlist,
    affordableWaitlistPropertySetting,
    isAffordableWaitlistPropertySettingStatusClosed,
  } = useAffordableWaitlistValidations();
  const enabledAffordableWaitlist =
    affordableWaitlistFlag &&
    accountThisPropertyForWaitlist &&
    affordableWaitlistPropertySetting;

  const isNotHUDorRD = !(
    affordableProgramsArr?.includes('HUD') ||
    affordableProgramsArr?.includes('RD')
  );

  // On V2 the wizard decides program by program from the Application Date, so a closed
  // waitlist no longer keeps the household from converting.
  const isClosedForConvert =
    !affordableWaitlistV2 && isAffordableWaitlistPropertySettingStatusClosed;

  const convertDisabled =
    enabledAffordableWaitlist &&
    (!hasAffordableWaitlistPermissionAdd || isClosedForConvert);

  const enabledConventionalProcess =
    unitType === 'conventional' &&
    isAffordableWaitlistPropertySettingStatusClosed;

  const enabledAffordableProcess = unitType === 'affordable' && isNotHUDorRD;

  const isEnabled =
    enabledConventionalProcess || enabledAffordableProcess || !convertDisabled;

  const assignees = useMemo(() => {
    const assignee = currentRecord.assignedTo;

    // $FlowFixMe
    const assigneeIndex = findIndex(
      (x) => x.value === currentRecord.assignedToId,
      users,
    );

    const options =
      assigneeIndex < 0 && assignee
        ? sortBy(
            (x) => {
              return x.text.toLowerCase();
            },
            [
              {
                value: currentRecord.assignedToId,
                text: `${assignee.firstName} ${assignee.lastName}`,
              },
            ].concat(users),
          )
        : users;

    if (!currentRecord.assignedTo) {
      options.unshift({
        value: '',
        text: intl.formatMessage(messages.unassigned),
        disabled: true,
      });
    }
    return options;
  }, [currentRecord.assignedTo, currentRecord.assignedToId, users, intl]);

  const ConvertToApplicantButton = () => {
    const buttonProps = {
      variant: 'action',
      disabled: !isEnabled,
      children: <FormattedMessage {...messages.convertToApplicant} />,
    };
    let tooltipMessage = null;

    if (!isEnabled) {
      if (!hasAffordableWaitlistPermissionAdd) {
        tooltipMessage = intl.formatMessage(messages.waitlistNoPermission);
      } else if (isClosedForConvert) {
        tooltipMessage = intl.formatMessage(messages.waitlistClosed);
      }
    }

    return tooltipMessage ? (
      <Tooltip title={tooltipMessage} placement={'top'}>
        <Button {...buttonProps} />
      </Tooltip>
    ) : (
      <Button {...buttonProps} onClick={handleConvertToApplicant} />
    );
  };

  return (
    <Box padding={2}>
      <Title>
        <IconTitle
          icon={<i key="1" className="et-prospect" />}
          message={messages.snapshot}
        />
      </Title>
      <>
        <Row className="row">
          <Col xs={12} md={3}>
            <LabelProfileDetails>
              <FormattedMessage {...messages.assignedTo} />
            </LabelProfileDetails>
          </Col>
          <Col xs={12} md={8}>
            <ElementWithPermissions scope={['prospect-assign']}>
              <select
                id="assignedToId"
                name="assignedToId"
                value={
                  (initialValues && initialValues.assignedToId) ||
                  currentRecord.assignedToId ||
                  ''
                }
                onChange={(event) =>
                  handleAssignedToChange(event, event.target.value)
                }
              >
                {assignees.map((option) => (
                  <option
                    key={option.value || 'unassigned'}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.text}
                  </option>
                ))}
              </select>
            </ElementWithPermissions>
          </Col>
        </Row>
        <Divider />
        <DetailList>
          <FloorPlanDetailRow
            floorPlans={floorPlans}
            unitFloorPlanId={unit?.floorPlanId}
            preferredFloorPlanId={preferredFloorPlanId}
            propertyId={currentRecord.propertyId}
          />
          <li>
            <FormattedMessage {...messages.prospectCreationDate} />{' '}
            <FormattedDateOrDashes
              value={currentRecord.createdAt}
              format="MMM DD, YYYY"
            />
          </li>
          <li>
            <FormattedMessage {...messages.nextScheduledActivity} />{' '}
            {
              <FormattedDateOrDashes
                value={nextActivity.startTime}
                format="MMM DD, YYYY"
              />
            }
          </li>
          <li>
            <FormattedMessage {...messages.numberOfScheduledContacts} />
            {` ${numberOfScheduledContacts}`}
          </li>
          <li>
            <FormattedMessage {...messages.numberOfCompletedContacts} />
            {` ${numberOfCompletedContacts}`}
          </li>
          <li>
            <FormattedMessage {...messages.tour} />
            {` ${currentRecord.tourStatus}`}
          </li>
        </DetailList>
        <Row>
          <Col xs={12} sm={6}>
            <Button
              variant={'primarySubtle'}
              onClick={() =>
                history.push(
                  `/property/${currentRecord.propertyId}/manage-unit-availability?prospectId=${currentRecord.id}`,
                )
              }
              type="button"
            >
              <FormattedMessage {...messages.provideQuote} />
            </Button>
          </Col>
          <Col xs={12} sm={6}>
            <ConvertToApplicantButton />
          </Col>
        </Row>
      </>
    </Box>
  );
};

export default injectIntl(Snapshot);
